import type { RecentApplication } from 'tio-common/utils/tuition-assistance/application-states';

export interface AmountInfo {
  reimbursementAmount: number;
  reimbursementLabelKey: string;
  courseCashAmount: number;
  courseCashLabelKey: string;
  hasCourseCash: boolean;
}

/** Application states that are still in draft / not yet submitted */
export const REQUESTED_STATES = new Set([
  'TAS.ApplicationState.CREATED',
  'TAS.ApplicationState.DEFINE_COURSES',
]);

/** Application states awaiting review */
export const PENDING_STATES = new Set([
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

export const getHistoryDate = (application: RecentApplication) => {
  return (
    application.tasProgramInstanceCreatedAtDate ||
    application.tasApplicationCreatedAtDate ||
    application.documentCreatedAt ||
    application.createdAt
  );
};

/**
 * Get the display name for an application
 */
export const getApplicationName = (app: RecentApplication) => {
  const type = app.tasProgramTemplateTypeKeyword;
  if (type === 'TAS.ProgramType.2' || type === 'TAS.ProgramType.3') {
    return (
      app.tasApplicationFieldApplicationNameKeyword ||
      app.tasProgramInstanceFieldProgramSpecializationKeyword ||
      `Application #${app.tasApplicationIdKeyword || app.id}`
    );
  }
  return (
    app.tasApplicationFieldApplicationNameKeyword ||
    `Application #${app.tasApplicationIdKeyword || app.id}`
  );
};

/**
 * Get the display name for a program instance
 */
export const getInstanceName = (app: RecentApplication) => {
  return (
    app.tasProgramInstanceFieldProgramSpecializationKeyword ||
    app.tasProgramInstanceFieldSchoolInstitutionNameKeyword ||
    app.tasProgramTemplateFieldProgramNameKeyword ||
    'Program'
  );
};

/**
 * Get the program template name
 */
export const getProgramName = (app: RecentApplication) => {
  return app.tasProgramTemplateFieldProgramNameKeyword || 'Unknown Program';
};

/**
 * Compute amount info for a RecentApplication (OpenSearch fields, amounts in cents).
 * Shared between getting-started and benefit-history pages.
 */
export const applicationAmountInfo = (app: RecentApplication): AmountInfo | null => {
  const state = app.tasApplicationStateKeyword || '';
  const isRequestedState = REQUESTED_STATES.has(state);

  // Reimbursement amounts (in cents → dollars)
  const approvedTotal =
    (app.tasApplicationFieldFinalApprovedTotalInteger ?? app.paymentFinalApprovedTotalAmount ?? 0) /
    100;
  const requestedTotal =
    (app.tasApplicationFieldPreApprovedTotalInteger ?? app.paymentPreApprovedTotal ?? 0) / 100;
  const paidTotal = (app.paymentPaidAmount ?? 0) / 100;

  // CourseCash amounts (in cents → dollars)
  const courseCashPreApproved = (app.tasApplicationFieldCardPreApprovedTotalInteger ?? 0) / 100;
  const courseCashFinalApproved = (app.tasApplicationFieldCardFinalApprovedTotalInteger ?? 0) / 100;
  const courseCashApproved = isRequestedState
    ? courseCashPreApproved || courseCashFinalApproved
    : courseCashFinalApproved || courseCashPreApproved;
  const courseCashUsed = (app.courseCashUsedAmount ?? 0) / 100;
  const courseCashReserved = (app.courseCashReservedAmount ?? 0) / 100;

  const isCourseCashApp = courseCashApproved > 0 || courseCashUsed > 0 || courseCashReserved > 0;

  // CourseCash display: used → reserved → approved
  let courseCashAmount = courseCashUsed;
  let courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_used';
  if (courseCashUsed <= 0 && courseCashReserved > 0) {
    courseCashAmount = courseCashReserved;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_reserved';
  } else if (courseCashUsed <= 0 && courseCashReserved <= 0 && courseCashApproved > 0) {
    courseCashAmount = courseCashApproved;
    courseCashLabelKey = 'tuition_assistance.reimagined.index.application_amount_requested';
  }

  // Refund / repayment
  const refundAmount = (app.paymentRefundRequestedAmount ?? 0) / 100;
  if (refundAmount > 0) {
    return {
      reimbursementAmount: refundAmount,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_repayment',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  // Paid
  if (paidTotal > 0) {
    return {
      reimbursementAmount: paidTotal,
      reimbursementLabelKey: 'tuition_assistance.reimagined.index.application_amount_paid',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  // Pending review
  if (PENDING_STATES.has(state)) {
    const amount = approvedTotal > 0 ? approvedTotal : requestedTotal;
    if (amount <= 0 && !isCourseCashApp) return null;
    return {
      reimbursementAmount: amount,
      reimbursementLabelKey:
        approvedTotal > 0
          ? 'tuition_assistance.reimagined.index.application_amount_pending'
          : 'tuition_assistance.reimagined.index.application_amount_requested',
      courseCashAmount,
      courseCashLabelKey,
      hasCourseCash: isCourseCashApp,
    };
  }

  // Fallback: approved → requested
  const reimbursementAmount = approvedTotal > 0 ? approvedTotal : requestedTotal;
  if (reimbursementAmount <= 0 && !isCourseCashApp) return null;
  return {
    reimbursementAmount,
    reimbursementLabelKey:
      approvedTotal > 0
        ? 'tuition_assistance.reimagined.index.application_amount_approved'
        : 'tuition_assistance.reimagined.index.application_amount_requested',
    courseCashAmount,
    courseCashLabelKey,
    hasCourseCash: isCourseCashApp,
  };
};
